import { Typography, Box } from '@mui/material';
import { useMemo } from 'react';
import { RecordContainer } from '../Styles';
import { SelectOption } from '@act/storybook';
import { FilterOptionDto, SurveyV2 } from '@act/shared/data-transfer-objects';
import { FormBuilder } from '@act/storybook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { daysOffsetOptions, maxMonthOptions } from './constants';
import { ManageSegmentOverrides } from './ManageSegmentOverrides';
import { Control, FormState } from 'react-hook-form';

interface AssignmentConfigFormConfigProps {
  clientOptions: FilterOptionDto[];
  segmentOptions: FilterOptionDto[];
  stageOptions: FilterOptionDto[];
  segmentOverrides: {
    segmentId: number;
    maxMonth?: number;
    offsetValue: 'avgProgramDays' | number;
  }[];
  setSegmentOverrides: (
    overrides: {
      segmentId: number;
      maxMonth?: number;
      offsetValue: 'avgProgramDays' | number;
    }[]
  ) => void;
  control: Control<SurveyV2.UpdateAssignmentConfigRequest>;
  formState: FormState<SurveyV2.UpdateAssignmentConfigRequest>;
}

export const useAssignmentConfigFormConfig = ({
  clientOptions,
  segmentOptions,
  stageOptions,
  segmentOverrides,
  setSegmentOverrides,
  control,
  formState,
}: AssignmentConfigFormConfigProps): FormConfig<SurveyV2.UpdateAssignmentConfigRequest> => {
  return useMemo(
    () => [
      {
        type: 'label',
        label: 'Assignment Criteria',
      },
      {
        type: 'input',
        name: 'assignmentConfiguration.clientIds',
        label: 'Customers',
        input: 'autocomplete',
        props: {
          options: clientOptions,
          getOptionLabel: (o: FilterOptionDto) => o?.label,
          multiple: true,
          style: {
            paddingBottom: '16px',
          },
        },
      },
      {
        type: 'input',
        name: 'assignmentConfiguration.segmentIds',
        label: 'Segments',
        input: 'autocomplete',
        props: {
          options: segmentOptions,
          getOptionLabel: (o: FilterOptionDto) => o?.label,
          multiple: true,
          style: { paddingBottom: '16px' },
        },
      },
      {
        type: 'input',
        name: 'assignmentConfiguration.stageIds',
        label: 'Stages',
        input: 'autocomplete',
        props: {
          options: stageOptions,
          getOptionLabel: (o: FilterOptionDto) => o?.label,
          multiple: true,
          style: { paddingBottom: '16px' },
        },
      },
      {
        type: 'passthrough',
        content: (
          <RecordContainer>
            <Typography variant="bodyMedium">
              Assignment Window (optional)
            </Typography>
            <Box display="flex" flexDirection="row" width="100%">
              <Box display="flex" flexDirection="column" width="50%">
                <FormBuilder
                  config={[
                    {
                      type: 'input',
                      name: 'assignmentConfiguration.availableWindow.start',
                      label: 'First Month Assignable',
                      input: 'select',
                      props: {
                        options: maxMonthOptions,
                        fullWidth: true,
                      },
                    },
                  ]}
                  control={control}
                  formState={formState}
                />
              </Box>
              <Typography
                variant="bodyLarge"
                style={{ padding: '0px 8px', alignSelf: 'center' }}
              >
                to
              </Typography>
              <Box display="flex" flexDirection="column" width="50%">
                <FormBuilder
                  config={[
                    {
                      type: 'input',
                      name: 'assignmentConfiguration.availableWindow.end',
                      label: 'Last Month Assignable',
                      input: 'select',
                      props: {
                        options: maxMonthOptions,
                        fullWidth: true,
                      },
                    },
                  ]}
                  control={control}
                  formState={formState}
                />
              </Box>
            </Box>
          </RecordContainer>
        ),
      },
      {
        type: 'label',
        label: 'Due Date Criteria',
      },
      {
        type: 'input',
        name: 'dueAtConfig.offsetValue',
        label: 'Assignment Offset Days',
        input: 'select',
        props: {
          options: daysOffsetOptions as SelectOption<
            number | 'avgProgramDays'
          >[],
          fullWidth: true,
        },
      },
      {
        type: 'input',
        name: 'dueAtConfig.maxMonth',
        label: 'Last Month Available (optional)',
        input: 'select',
        props: {
          options: maxMonthOptions,
          fullWidth: true,
        },
      },
      {
        type: 'input',
        name: 'dueAtConfig.fixedDate',
        label: 'Fixed Date (optional)',
        input: 'date',
        props: {
          inputType: 'date',
        },
      },
      {
        type: 'passthrough',
        content: (
          <ManageSegmentOverrides
            overrides={segmentOverrides || []}
            handleOverridesUpdated={setSegmentOverrides}
            segmentOptions={segmentOptions}
          />
        ),
      },
    ],
    [
      clientOptions,
      segmentOptions,
      stageOptions,
      segmentOverrides,
      setSegmentOverrides,
      control,
      formState,
    ]
  );
};
